import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { CoachCategoryDefaultSettingsForm } from "@modules/coachCategories/components/coachCategoryDefaultSettingsForm"
import { CoachCategoryInfoForm } from "@modules/coachCategories/components/coachCategoryInfoForm"
import { CoachCategoryResumeView } from "@modules/coachCategories/components/coachCategoryResumeView"
import { CoachCategoryEntity } from "@modules/coachCategories/entities/coachCategoryEntity"
import {
  getCoachCategoryDefaultSettingsDraftValues,
  getCoachCategoryDefaultSettingsSchema,
  getCoachCategoryInfoDraftValues,
  getCoachCategoryInfoSchema,
} from "@modules/coachCategories/formTypes/coachCategoryFormTypes"
import type { CategorySlotDefaultValueDto } from "@modules/coachCategories/gateway/coachCategoriesDto"
import {
  useCoachCategories,
  useCoachCategorySlotDefaults,
  useCreateCoachCategory,
  useUpdateCoachCategory,
} from "@modules/coachCategories/hooks/useCoachCategories"
import { word } from "@modules/core/utils/i18n"
import { cn } from "@modules/lib/utils"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useImperativeHandle, useRef, useState } from "react"

const FormStep = {
  INFO: "Info",
  DEFAULT_SETTINGS: "DefaultSettings",
  RESUME: "Resume",
} as const
type FormStep = (typeof FormStep)[keyof typeof FormStep]

export type CoachCategoryFormViewRef = {
  onClose: () => Promise<boolean>
}

export function CoachCategoryFormView({
  categoryId,
  ref,
  className,
}: {
  categoryId?: string
  ref: React.Ref<CoachCategoryFormViewRef>
  className?: React.ComponentProps<"div">["className"]
}) {
  const { data: categories } = useCoachCategories()
  const existingCategory = categoryId ? categories.find((c) => c.id === categoryId) : undefined

  if (categoryId) {
    return (
      <CoachCategoryFormViewWithSlotDefaults
        ref={ref}
        className={className}
        categoryId={categoryId}
        existingCategory={existingCategory}
      />
    )
  }

  return <CoachCategoryFormViewContent ref={ref} className={className} existingCategory={existingCategory} />
}

function CoachCategoryFormViewWithSlotDefaults({
  categoryId,
  existingCategory,
  ref,
  className,
}: {
  categoryId: string
  existingCategory?: CoachCategoryEntity
  ref: React.Ref<CoachCategoryFormViewRef>
  className?: string
}) {
  const { data: existingSlotDefaults } = useCoachCategorySlotDefaults(categoryId)

  return (
    <CoachCategoryFormViewContent
      ref={ref}
      className={className}
      existingCategory={existingCategory}
      existingSlotDefaults={existingSlotDefaults}
    />
  )
}

function CoachCategoryFormViewContent({
  existingCategory,
  existingSlotDefaults,
  ref,
  className,
}: {
  existingCategory?: CoachCategoryEntity
  existingSlotDefaults?: CategorySlotDefaultValueDto
  ref: React.Ref<CoachCategoryFormViewRef>
  className?: string
}) {
  const [formStep, setFormStep] = useState<FormStep>(FormStep.INFO)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const confirmPopup = useConfirmPopup()
  const { hideSidebar } = useSidebar()

  const { mutateAsync: createCategory } = useCreateCoachCategory()
  const { mutateAsync: updateCategory } = useUpdateCoachCategory(existingCategory?.id ?? "")

  const containerRef = useRef<HTMLDivElement>(null)

  const infoForm = useAppForm({
    defaultValues: existingCategory ? existingCategory.toInfoFormValues() : getCoachCategoryInfoDraftValues(),
    validators: { onSubmit: getCoachCategoryInfoSchema() },
    onSubmit: () => {
      scrollToTop()
      setFormStep(FormStep.DEFAULT_SETTINGS)
    },
  })

  const defaultSettingsForm = useAppForm({
    defaultValues: existingSlotDefaults
      ? CoachCategoryEntity.toDefaultSettingsFormValues(existingSlotDefaults)
      : getCoachCategoryDefaultSettingsDraftValues(),
    validators: { onSubmit: getCoachCategoryDefaultSettingsSchema() },
    onSubmit: () => {
      scrollToTop()
      setFormStep(FormStep.RESUME)
    },
  })

  useImperativeHandle(ref, () => ({
    onClose: async () => {
      if (!infoForm.state.isPristine || !defaultSettingsForm.state.isPristine) {
        const confirm = await new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("coach.planning.category.form.closeMessage.title"),
            message: word("coach.planning.category.form.closeMessage.message"),
            accept: () => {
              resolve(true)
            },
            cancel: () => {
              resolve(false)
            },
            footerProps: { confirmText: word("global.ok") },
          })
        })
        return confirm
      }
      return Promise.resolve(true)
    },
  }))

  const scrollToTop = () => {
    document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
  }

  const scrollToFirstError = () => {
    const errorElements = containerRef.current?.querySelectorAll('[aria-invalid="true"]')
    if (errorElements && errorElements.length > 0) {
      document
        .querySelector('[data-slot="right-sidebar-content"]')
        ?.scrollTo({ top: (errorElements[0] as HTMLElement).offsetTop - 30, behavior: "smooth" })
    }
  }

  const moveToDefaultSettingsStep = async () => {
    const errors = await infoForm.validateAllFields("submit")
    if (errors.some(Boolean)) {
      scrollToFirstError()
      return
    }
    await infoForm.handleSubmit()
  }

  const moveToResumeStep = async () => {
    const errors = await defaultSettingsForm.validateAllFields("submit")
    if (errors.some(Boolean)) {
      scrollToFirstError()
      return
    }
    await defaultSettingsForm.handleSubmit()
  }

  const onSave = async () => {
    setIsSubmitting(true)
    try {
      const input = {
        program: CoachCategoryEntity.fromFormValuesToProgramInputDto(infoForm.state.values, existingCategory),
        slotDefaults: CoachCategoryEntity.fromFormValuesToSlotDefaultsInputDto(defaultSettingsForm.state.values),
      }
      if (existingCategory) {
        await updateCategory(input)
        toast.success(word("coach.planning.category.form.update.success"), {
          classNames: successToastClassName(),
        })
      } else {
        await createCategory(input)
        toast.success(word("coach.planning.category.form.create.success"), {
          classNames: successToastClassName(),
        })
      }
      hideSidebar(false)
    } catch (e: unknown) {
      const error = e as { message: string }
      toast.error(word("global.error.label"), {
        description: error.message,
        classNames: errorToastClassName(),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div ref={containerRef} className={cn("flex w-full flex-col", className)}>
      {formStep === FormStep.INFO && (
        <CoachCategoryInfoForm
          form={infoForm}
          isEditing={!!existingCategory}
          onValidate={() => void moveToDefaultSettingsStep()}
        />
      )}
      {formStep === FormStep.DEFAULT_SETTINGS && (
        <CoachCategoryDefaultSettingsForm
          form={defaultSettingsForm}
          categoryName={infoForm.state.values.name}
          categoryColor={infoForm.state.values.color}
          moveToPreviousFormStep={() => {
            setFormStep(FormStep.INFO)
            scrollToTop()
          }}
          onValidate={() => void moveToResumeStep()}
          isSubmitting={isSubmitting}
        />
      )}
      {formStep === FormStep.RESUME && (
        <CoachCategoryResumeView
          infoValues={infoForm.state.values}
          settingsValues={defaultSettingsForm.state.values}
          moveToPreviousFormStep={() => {
            setFormStep(FormStep.DEFAULT_SETTINGS)
            scrollToTop()
          }}
          onSave={() => void onSave()}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  )
}
